import {
  bookingServiceSchema,
  createSection,
  faqBlockSchema,
  featureBlockSchema,
  type Section,
} from '@/features/storefront/sections/schema';
import type { SectionPreset } from '@/previewThemes/types';

/**
 * Serra's own shelf of the "add section" library.
 *
 * This theme dresses a house that takes appointments - a salon, a clinic, a
 * studio - and the shop's generic sections can all express that, but only
 * after a merchant has worked out that "what to know before you come" is an
 * FAQ and that the calendar is a `bookings` section with its opening hours in
 * it. These presets are that translation, made once by whoever designed the
 * theme. Each one is an ordinary section, arranged and worded the way this
 * design intends it, and from the moment it lands on the page it is edited,
 * moved, hidden and rendered by exactly the same code as everything else.
 *
 * Two rules they follow, the same two Feast's do:
 *
 * - *no photography*. A preset ships copy and arrangement, never a picture
 *   from the theme's demo shoot. A merchant who published before noticing
 *   would be advertising their salon under another salon's work, and they
 *   would find out from a client;
 * - *placeholder copy that is a label, never a claim*. "A service you offer,
 *   and how long it takes" is a prompt; "Balayage, three hours, 7,500" is a
 *   sentence about a business this theme knows nothing about. That matters
 *   more here than anywhere else in the library, because the section it
 *   applies to takes real appointments the moment it is published.
 */

/** Where a booking form earns its place: the pages somebody decides on. */
const DECIDING = ['home', 'product', 'listing', 'category'] as const;

export const SERRA_PRESETS: readonly SectionPreset[] = [
  {
    id: 'appointments',
    label: 'Appointments',
    description: 'Your services, your opening hours, and a calendar that takes the booking.',
    type: 'bookings',
    pages: DECIDING,
    build: (): Section => ({
      ...createSection('bookings'),
      title: 'Book an appointment',
      subtitle: 'Pick a service and a time. We confirm by message.',
      // Three prompts rather than one, because the shape of the list is half
      // of what a merchant is being shown - a short service, a long one, and
      // the consultation that has no price on it.
      blocks: [
        bookingServiceSchema.parse({
          name: 'A service you offer',
          description: 'One line about what it involves.',
          duration_minutes: 45,
        }),
        bookingServiceSchema.parse({
          name: 'A longer appointment',
          description: 'The one that takes an afternoon.',
          duration_minutes: 180,
        }),
        bookingServiceSchema.parse({
          name: 'A consultation',
          description: 'Priced on the day, so leave the price empty.',
          duration_minutes: 20,
        }),
      ],
      columns: 3,
      // The salon week this theme is drawn around: shut on Monday, late on
      // Friday and Saturday. A merchant's own week is two clicks away, and a
      // section that arrived open seven days would be wrong in a way nobody
      // checks until somebody turns up.
      hours: [
        { weekday: 0, closed: true, opens: '10:00', closes: '20:00' },
        { weekday: 1, closed: false, opens: '10:00', closes: '20:00' },
        { weekday: 2, closed: false, opens: '10:00', closes: '20:00' },
        { weekday: 3, closed: false, opens: '10:00', closes: '21:00' },
        { weekday: 4, closed: false, opens: '10:00', closes: '21:00' },
        { weekday: 5, closed: false, opens: '09:00', closes: '21:00' },
        { weekday: 6, closed: false, opens: '09:00', closes: '19:00' },
      ],
      lead_time_hours: 3,
      notes_label: 'Anything we should know?',
      button_label: 'Request this appointment',
      padding: 'lg',
    }),
  },
  {
    id: 'before-you-come',
    label: 'Before you come',
    description: 'The three things a first-time client should read before they book.',
    type: 'faq',
    pages: ['home', 'product'],
    build: (): Section => ({
      ...createSection('faq'),
      title: 'Before you come',
      blocks: [
        faqBlockSchema.parse({
          question: 'How far ahead should I book?',
          answer: 'Say how much notice each kind of appointment usually needs.',
        }),
        faqBlockSchema.parse({
          question: 'Is there anything to do beforehand?',
          answer: 'Patch tests, arriving with clean hair, what to bring - whatever applies.',
        }),
        faqBlockSchema.parse({
          question: 'What if I need to move it?',
          answer: 'Your cancellation window, and what happens inside it.',
        }),
      ],
      padding: 'lg',
    }),
  },
  {
    id: 'how-it-works',
    label: 'How an appointment works',
    description: 'Four steps between booking and leaving, as a promises strip.',
    type: 'features',
    pages: ['home', 'product'],
    build: (): Section => ({
      ...createSection('features'),
      columns: 4,
      blocks: [
        featureBlockSchema.parse({
          icon: 'clock',
          title: 'Your opening hours',
          body: 'The days and times, in one line.',
        }),
        featureBlockSchema.parse({
          icon: 'sparkles',
          title: 'What happens first',
          body: 'The consultation, or whatever your first ten minutes are.',
        }),
        featureBlockSchema.parse({
          icon: 'shield',
          title: 'What you check',
          body: 'Patch tests, allergies, anything you will not skip.',
        }),
        featureBlockSchema.parse({
          icon: 'heart',
          title: 'How you confirm',
          body: 'Message, call, or an email - and how quickly.',
        }),
      ],
      background: 'surface',
      padding: 'sm',
    }),
  },
];
